import { auth, functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc as firestoreDoc, updateDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { getPatientReportContentPathSegments } from '../constants/adultAutismFirestorePaths';
import { STAGE_TO_REPORT_PREFIX } from '../constants/adultAutismStages';

/**
 * Trigger Adult Autism document template rendering for a given assessment stage.
 *
 * This is the Autism counterpart to TriggerRenderDocTemplate.js (ADHD). The key
 * architectural differences mirror the rest of the Autism pipeline:
 *
 *   - Status lives on the MODULAR reportContent subcollection document
 *     (patients/{id}/adult_autism/assessment_001/reportContent/{stageKey}) as
 *     `{prefix}ReportProcessingStatus`, not on the monolithic patient doc's
 *     dataModel.6_manageReports map.
 *   - A single generic Cloud Function (renderAndStoreAutismDocument) handles all
 *     stages; we pass `stage_key` so the backend registry dispatches correctly.
 *
 * Why a transaction: we atomically guard against duplicate invocations. If the
 * status is already 'processing' (e.g. a prior call that hasn't finished), we
 * bail out instead of clobbering a 'complete' value that may have just arrived.
 * A `{prefix}ReportProcessingStartedAt` timestamp is written alongside so the
 * dashboard's self-healing logic can detect states stuck on 'processing'.
 *
 * @param {Object} params
 * @param {string} params.patientId   - Patient Firestore document ID.
 * @param {string} params.stageKey    - Autism stage key, e.g. 'history'.
 * @param {string} params.clinicianId - UID of the clinician triggering the render
 *                                       (must equal the authenticated user).
 * @returns {Promise<{success: boolean, data?: object, error?: any}>}
 */
export const TriggerRenderAutismDocTemplate = async ({ patientId, stageKey, clinicianId }) => {
  const renderAndStoreAutismDocument = httpsCallable(functions, 'renderAndStoreAutismDocument', {
    timeout: 360000, // 6 minutes (slightly longer than the 5-minute server timeout)
  });

  const user = auth.currentUser;
  if (!user || !user.uid) return { success: false, error: 'User not authenticated' };

  if (!patientId || !stageKey || !clinicianId) {
    console.error('Missing required parameters:', { patientId, stageKey, clinicianId });
    return { success: false, error: 'Missing required parameters' };
  }

  const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
  if (!prefix) {
    console.error('Unknown autism stage key:', stageKey);
    return { success: false, error: 'Unknown stage key' };
  }

  const statusField = `${prefix}ReportProcessingStatus`;
  const startedAtField = `${prefix}ReportProcessingStartedAt`;

  try {
    const reportContentRef = firestoreDoc(db, ...getPatientReportContentPathSegments(patientId, stageKey));

    // Atomically check-then-set: only transition to 'processing' when not
    // already processing. Prevents a second call from overwriting a 'complete'
    // that arrived between calls.
    let alreadyProcessing = false;
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(reportContentRef);
      const current = snap.data()?.[statusField];
      if (current === 'processing') {
        alreadyProcessing = true;
        return;
      }
      transaction.update(reportContentRef, {
        [statusField]: 'processing',
        [startedAtField]: serverTimestamp(),
      });
    }, { maxAttempts: 10 });

    if (alreadyProcessing) {
      console.log(`Skipping ${stageKey} autism render — already processing`);
      return { success: true, data: { status: 'already-processing' } };
    }

    // Fire and forget: the CF flips the status to 'complete' or 'error' itself.
    renderAndStoreAutismDocument({
      patient_id: patientId,
      stage_key: stageKey,
      clinician_id: clinicianId,
    }).then((result) => {
      console.log('Autism document generated successfully:', result.data);
    }).catch((error) => {
      console.error('Error in renderAndStoreAutismDocument function:', error);

      // deadline-exceeded means the client timed out but the CF is still
      // running server-side — don't flip to 'error'. The dashboard's
      // self-healing timer will catch a genuine failure.
      if (error.code === 'deadline-exceeded') {
        console.log('Client-side timeout occurred, but function continues processing in background');
        return;
      }

      updateDoc(reportContentRef, {
        [statusField]: 'error',
      }).catch((updateError) => {
        console.error('Error updating autism processing status:', updateError);
      });
    });

    return { success: true, data: { status: 'processing' } };
  } catch (error) {
    console.error('Error setting autism processing status:', error);
    return { success: false, error };
  }
};
